import { useContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHref, useLocation, useNavigate, useParams } from 'react-router-dom'

import { getPostById } from '../api'
import { NotificationsContext } from '../app/contexts'
import { useAppUser, useCurrentSite, useParentViewCapability } from '../app/hooks'
import { showUpsell } from '../app/utilities'
import { CAPABILITY_ACCESS_ALL_ABOUT_ME_DOCUMENT, CAPABILITY_VIEW_GOAL } from '../arch/capabilityConstants'
import { CAN_BE_NAVIGATOR, HTTP_ACCEPTED } from '../arch/constants'
import { PLAN_TYPE_504, PLAN_TYPE_ROUTE_ROOTS } from '../arch/iepConstants'
import { postFilename } from '../binder/utilities'
import { emptyArray, equalArrayWithOrder, getHookPromiseErrorHandler, idMap } from '../common'
import { useDocumentTags } from '../commonHooks'
import { PLAN_EXTRACTION_FUNCTIONS, PLAN_PRINT_FUNCTIONS, getIep, getIpp } from '../domain/plan'
import { PlanNavigationContext } from '../goal/contexts'
import model from '../model'

import {
  GOAL_LOCATION_WORKSPACE,
  trackIepNotesPrinted,
  trackPlanReviewStarted,
  trackUpgradeFromIepOr504Assistant
} from '../tracking/segment'

import { tagFor } from '../typeUtilities'

import {
  IEP_DOCUMENT_TAG_IDS,
  IPP_DOCUMENT_TAG_IDS,
  PLAN_DEFAULT,
  PLAN_LABEL_504,
  PLAN_LABEL_IEP,
  PLAN_VARIANTS,
  SECTION_KEY_ACCOMMODATIONS,
  SECTION_KEY_GOALS,
  SECTION_KEY_MODIFICATIONS,
  SECTION_KEY_SERVICES
} from './constants'

import { IepAccessContext, IepWizardContext } from './contexts'
import { createBlankItem } from './extraction-editor/utilities'

import {
  getPercentReviewed,
  getVisibleRecommendationsAndConcerns,
  getWizardContinuationPath,
  getWizardSequence,
  pipelineStatusToPercent
} from './utilities'

import IepSectionItemEditDrawer from './support/IepSectionItemEditDrawer'

const EMPTY_TAGS = []
const RETRY_DELAY = 30 * 1000

const IEP_STATUS_LOADING = 'LOADING'
const IEP_STATUS_QUEUED = 'QUEUED'

const IEP_STATUS_MESSAGES = {
  [IEP_STATUS_LOADING]: 'Loading…',
  [IEP_STATUS_QUEUED]: 'Queued…'
}

const pathToTrackLocation = path =>
  path.includes('feed') ? 'Home' : path.includes('roadmaps') && path.includes('goals') ? GOAL_LOCATION_WORKSPACE : path

/**
 * This hook creates a convenience/replacement context for plan navigation that changes the root for
 * plan paths.
 */
const substitutePlanTypeRoot = (path, planTypeRoot) => path.replace('/edu/', `/${planTypeRoot}/`)

// path defaults to the /edu/ root, which only works for IEPs and 504s. A caller that hasn't scoped its
// own navigation context to the loaded plan's actual type (e.g. home feed) needs this to route IPPs to
// their own root instead of falling through to the IEP/504 one.
const rootedPlanPath = (path, planType) => {
  const planTypeRoot = PLAN_TYPE_ROUTE_ROOTS[planType]
  return planTypeRoot ? substitutePlanTypeRoot(path, planTypeRoot) : path
}

/**
 * Builds the location where the review of the given plan should continue, under its plan type’s own root.
 *
 * @param {object} plan the plan whose review to continue
 * @param {function} iepPath the `iepPath` from PlanNavigationContext
 * @param {string} from the path to come back to from the review
 * @returns the `{ to, state }` location for the plan’s wizard continuation
 */
const planContinuationLocation = (plan, iepPath, from) => {
  const { childId, id, planType } = plan

  return {
    to: rootedPlanPath(`${iepPath(id, childId)}/${getWizardContinuationPath(plan)}`, planType),
    state: { from }
  }
}

const PLAN_TYPES_BY_ROUTE_ROOT = Object.fromEntries(
  Object.entries(PLAN_TYPE_ROUTE_ROOTS).map(([planType, root]) => [root, planType])
)

// The plan type a pathname’s root segment names (e.g., /504s/… is a 504), or undefined off the plan routes.
const planTypeFromPathname = pathname => PLAN_TYPES_BY_ROUTE_ROOT[pathname.split('/')[1]]

const usePlanTypeNavigationContextProvider = planTypeRoot => {
  const planNavigationContext = useContext(PlanNavigationContext)
  const { iepPath, iepUploadPath } = planNavigationContext

  const planTypeNavigationContext = useMemo(
    () => ({
      ...planNavigationContext,

      iepPath: (iepId, clientId) => substitutePlanTypeRoot(iepPath(iepId, clientId), planTypeRoot),
      iepUploadPath: clientId => substitutePlanTypeRoot(iepUploadPath(clientId), planTypeRoot)
    }),

    [planNavigationContext, iepPath, iepUploadPath, planTypeRoot]
  )

  return planTypeNavigationContext
}

/**
 * This hook sets up certain values that are typically needed by the overall “wizard” session,
 * mostly simple relays to the current wizard context.
 *
 * @param {object} parameters defining the context/location of the caller within the overall IEP wizard
 *
 * - back: string represents the path to the step “before” the caller; due to the way React Router operates,
 *                only a single leading `..` is supported (the hook internally substitutes that with the
 *                path right before the current location)
 *
 *                If `back` is not supplied, the IEP wizard will not display a back button
 *
 * - progress: number represents how far down the wizard this caller is (0–100)
 * - title: string is the displayed title of the caller
 */
const useWizardSetup = ({ back, title, progress }) => {
  const wizardContext = useContext(IepWizardContext)

  // As stated above, `..`s are not handled as expected by the wizard; the hook has this code to at least
  // handle a single leading `..`.
  const location = useLocation()
  const { pathname } = location
  const pathSegments = pathname.split('/')
  const backPrefix = pathSegments.slice(0, pathSegments.length - 1).join('/')
  const backPath = back?.replace(/^\.\./, backPrefix)

  useEffect(() => {
    const { setBack, setProgress, setTitle } = wizardContext

    setBack(backPath)
    setProgress(progress)

    // Callers without a title of their own leave the wizard’s existing one in place.
    if (title) {
      setTitle(title)
    }
  }, [wizardContext, progress, title, backPath])
}

// Maps a section key to the matching "with recommendations" sequence property returned by
// getWizardSequence — used to splice a newly saved item into that section's frozen sequence (see
// useIepSectionEditDrawer.handleSaveDrawerSettled). PLOPs have no such sequence/wizard step.
const WITH_RECOMMENDATIONS_SEQUENCE_KEY_BY_SECTION = {
  [SECTION_KEY_GOALS]: 'goalWithRecommendationsSequence',
  [SECTION_KEY_SERVICES]: 'serviceWithRecommendationsSequence',
  [SECTION_KEY_ACCOMMODATIONS]: 'accommodationWithRecommendationsSequence',
  [SECTION_KEY_MODIFICATIONS]: 'modificationWithRecommendationsSequence'
}

const useIepAccess = () => {
  const { isUserCapable } = useParentViewCapability()
  const { canAccessIep } = useContext(IepAccessContext)

  return {
    canAccessIep: canAccessIep ?? isUserCapable(CAPABILITY_ACCESS_ALL_ABOUT_ME_DOCUMENT),
    canViewGoal: isUserCapable(CAPABILITY_VIEW_GOAL)
  }
}

/**
 * Returns the click handler for IEP upgrade prompts: it tracks the upgrade start for the given plan
 * type, then opens the upsell and, once the user has upgraded, returns them to the current page with
 * an upgrade-success state that carries the plan and client being viewed. The plan and child default to the
 * route’s params; a host that knows them from elsewhere passes them in.
 */
const useIepUpgrade = ({ planType: planTypeOverride, iepId: iepIdOverride, clientId: clientIdOverride } = {}) => {
  const { clientId: routeClientId, iepId: routeIepId } = useParams()
  const location = useLocation()
  const { pathname } = location

  const navigate = useNavigate()

  // A host that doesn’t know the plan type (or renders before the plan loads) still tracks the type the route names.
  const planType = planTypeOverride ?? planTypeFromPathname(pathname)

  // The plan and child come from the route on the wizard pages; hosts elsewhere (status trackers, rundowns) say
  // which plan they show, so that the success dialog can still lead to it.
  const iepId = iepIdOverride ?? routeIepId
  const clientId = clientIdOverride ?? routeClientId

  const handleUpgrade = event => {
    trackUpgradeFromIepOr504Assistant({ planType })

    showUpsell(location, navigate, {
      redirectDestination: { pathname, state: { iepUpgradeSuccess: true, iepId, clientId } }
    })
  }

  return handleUpgrade
}

/**
 * Encapsulates everything a wizard step needs to let a user edit an individual extracted section
 * item (IEP/504 Goal, Service, Accommodation, Modification, or PLOP; IPP Priority, Outcome, Goal,
 * Service, or Support) via IepSectionItemEditDrawer: which item is currently open in the drawer,
 * callbacks for opening and closing the drawer, and a ready-to-render JSX element representing the
 * drawer. The plan's own type selects the extraction endpoints; callers whose plan is not an IEP
 * must supply the matching planGetter/planModeler (e.g., the domain getIpp and model.ipp) for the
 * post-save refresh.
 *
 * TODO Rename to usePlanSectionEditDrawer (and the drawer to a Plan-prefixed name) once the
 *      surrounding UND-5162 work settles—this hook is no longer IEP-specific.
 *
 * Edit access is gated on `isNavigatorOrSuperuser` from IepWizardContext rather than a broader
 * "any non-parent user" check — the drawer can only persist extraction edits for Navigator, Admin,
 * or Superuser users (see IepSectionItemEditDrawer), so any wider gate would let other roles open
 * the drawer, "save", and have their edits silently discarded.
 */
const useIepSectionEditDrawer = ({
  iepId,
  iep,
  setIep,
  planGetter = getIep,
  planModeler = model.iep,
  onDelete,
  onSave,
  onError,
  setInitialSequence,
  withRecommendationsOnly
}) => {
  const { isNavigatorOrSuperuser } = useContext(IepWizardContext)

  // The plan's own type selects the extraction endpoints; a plan type with no extraction behind it
  // (absent from the table) keeps extraction editing off entirely.
  const { planType } = iep ?? {}
  const extractionFunctions = PLAN_EXTRACTION_FUNCTIONS[planType]

  const [editDrawerOpen, setEditDrawerOpen] = useState(false)
  const [editItem, setEditItem] = useState(null)
  const [editSectionKey, setEditSectionKey] = useState(null)

  const handleEditItem = (item, sectionKey) => {
    setEditItem(item)
    setEditSectionKey(sectionKey)
    setEditDrawerOpen(true)
  }

  const handleAddItem = (sectionKey, insertAfterId) =>
    handleEditItem(createBlankItem(sectionKey, iep, insertAfterId), sectionKey)

  const handleCloseEditDrawer = () => {
    setEditDrawerOpen(false)
    setEditItem(null)
    setEditSectionKey(null)
  }

  const handleSaveDrawerSettled = async savedItem => {
    // Capture before handleCloseEditDrawer() below resets it.
    const savedSectionKey = editSectionKey
    const { insertAfterId } = editItem ?? {}
    const { id: savedItemId, uniqueIdentifier, unique_identifier } = savedItem ?? {}
    const savedUniqueId = unique_identifier || uniqueIdentifier || savedItemId

    try {
      const iepResponse = await planGetter(iepId)
      const nextIep = planModeler(iepResponse)
      handleCloseEditDrawer()
      setIep(nextIep)

      // The frozen with-recommendations sequence (see GoalConcernsSubstep, ServicesStep,
      // AccommodationsStep, ModificationsStep) is only ever pruned on delete; a newly added item
      // must be spliced in here too, or navigating straight to it shows it missing from the
      // sequence (e.g. goalIndex resolves to -1, rendering as "Goal 0 of N").
      const sequenceKey = WITH_RECOMMENDATIONS_SEQUENCE_KEY_BY_SECTION[savedSectionKey]

      if (withRecommendationsOnly && setInitialSequence && sequenceKey) {
        const nextList = nextIep[savedSectionKey] || []
        const matchedItem = nextList.find(
          item =>
            (savedUniqueId && String(item?.uniqueIdentifier) === String(savedUniqueId)) ||
            (savedItemId && String(item?.id) === String(savedItemId))
        )
        const nextItemId = matchedItem?.id
        const sectionSequence = getWizardSequence(nextIep)[sequenceKey]

        // Only add the newly created item to the active walkthrough sequence if it has recommendations
        // (and thus is included in the filtered sectionSequence). If it doesn't have any recommendations,
        // we omit it since the user is in the "Only recommendation" filtered view.
        if (nextItemId !== null && nextItemId !== undefined && sectionSequence.includes(nextItemId)) {
          setInitialSequence(current => {
            if (current.includes(nextItemId)) {
              return current
            }

            const index = insertAfterId ? current.indexOf(insertAfterId) : -1
            return index !== -1
              ? [...current.slice(0, index + 1), nextItemId, ...current.slice(index + 1)]
              : [...current, nextItemId]
          })
        }
      }

      if (onSave) {
        onSave(savedItem, nextIep)
      }
    } catch (error) {
      if (onError) {
        onError(error)
      }
    }
  }

  const handleDeleteDrawerSettled = async deletedItemId => {
    try {
      const iepResponse = await planGetter(iepId)
      const nextIep = planModeler(iepResponse)
      handleCloseEditDrawer()
      setIep({ ...nextIep })

      if (withRecommendationsOnly && setInitialSequence) {
        setInitialSequence(current => current.filter(id => id !== deletedItemId))
      }

      if (onDelete) {
        onDelete(deletedItemId, nextIep)
      }
    } catch (error) {
      if (onError) {
        onError(error)
      }
    }
  }

  const editDrawer = (
    <IepSectionItemEditDrawer
      open={editDrawerOpen}
      item={editItem}
      sectionKey={editSectionKey}
      documentId={iepId}
      planType={planType}
      extractionPatcher={extractionFunctions?.extractionPatcher}
      onClose={handleCloseEditDrawer}
      onSave={handleSaveDrawerSettled}
      onDelete={handleDeleteDrawerSettled}
      withRecommendationsOnly={withRecommendationsOnly}
    />
  )

  return {
    canEdit: isNavigatorOrSuperuser && Boolean(extractionFunctions),
    handleEditItem,
    handleAddItem,
    editDrawer
  }
}

/**
 * usePlanItemCoordination encapsulates the coordination between a list of plan items and their
 * recommendations: tracking the selected item, requesting that its first recommendation be shown,
 * and scrolling the item into view. The returned property names match PlanItemsList’s coordination
 * props so that the result can be spread directly into it (or one of its thin wrappers).
 */
const usePlanItemCoordination = () => {
  const [selectedItemId, setSelectedItemId] = useState()
  const [requestedRecommendationId, setRequestedRecommendationId] = useState()

  const selectAndScrollToItem = item => {
    const { id: itemId, recommendations = [] } = item ?? {}
    setSelectedItemId(itemId)

    // Propagate this to the recommendations, if any.
    setRequestedRecommendationId(recommendations[0]) // Will be undefined if recommendations is empty, and that’s OK.

    // Scroll the item’s row to the top: the row, rather than the card the click landed on, because
    // the row’s top is the natural position both of its columns pin from. Landing it at the
    // scroller’s padding puts the card flush beneath the pinned header and the pane’s content
    // level with it whatever their heights, and a column that is already pinned cannot distort
    // the distance. The element is looked up conditionally because callers can legitimately have
    // no item to open.
    const rowElement = itemId && document.getElementById(itemId)
    rowElement?.scrollIntoView({ behavior: 'smooth' })
  }

  const onItemClick = (event, item) => selectAndScrollToItem(item)
  const onItemOpen = item => selectAndScrollToItem(item)

  return { selectedItemId, requestedRecommendationId, onItemClick, onItemOpen }
}

/**
 * usePlanItemScrollPadding applies the given (JSS-generated) scroll-padding class to <html> for as
 * long as the caller is mounted. Scroll padding has to be set on the scroller itself—for these
 * steps, the viewport—so the class goes on the document element, leaving every other route’s
 * scrolling untouched.
 */
const usePlanItemScrollPadding = itemScrollerClass => {
  useEffect(() => {
    const { documentElement } = document

    documentElement.classList.add(itemScrollerClass)
    return () => documentElement.classList.remove(itemScrollerClass)
  }, [itemScrollerClass])
}

const NO_SEQUENCE = [] // Fixed empty array to minimize re-renders downstream.

/**
 * usePlanLoad encapsulates a wizard step’s plan load: fetching by ID, running the response through
 * the model, and tracking the loading state. The getter should come from the domain plan module so
 * that the read respects the write barrier.
 *
 * @param {object} parameters source of parameters
 *
 * - planId: the ID of the plan to load; a falsy ID resets the plan to null
 * - planGetter: function that fetches the plan by ID (e.g., the domain module’s getIep or getIpp)
 * - planModeler: function that shapes the API response for the UI (e.g., model.iep or model.ipp)
 * - withRecommendationsOnly: whether the step is filtering to items with recommendations
 * - withRecommendationsSequenceKey: the wizard sequence holding that filter’s item IDs
 * - onError: function to call if the load fails
 *
 * @returns { plan, setPlan, planLoading, initialWithRecommendationsSequence,
 *            setInitialWithRecommendationsSequence }
 */
const usePlanLoad = ({
  planId,
  planGetter,
  planModeler,
  withRecommendationsOnly,
  withRecommendationsSequenceKey,
  onError
}) => {
  const [plan, setPlan] = useState()
  const [planLoading, setPlanLoading] = useState(false)
  const [initialWithRecommendationsSequence, setInitialWithRecommendationsSequence] = useState(NO_SEQUENCE)

  useEffect(() => {
    if (!planId) {
      setPlan(null)
      setInitialWithRecommendationsSequence(NO_SEQUENCE)
      return
    }

    let active = true

    const retrievePlan = async () => {
      setPlanLoading(true)

      try {
        const planResponse = await planGetter(planId)
        if (!active) {
          return
        }

        const plan = planModeler(planResponse)
        setPlan(plan)
        setPlanLoading(false)

        // Special case memoization: We need to determine the sequence of items with recommendations
        // _at loading time_ because the items with visible recommendations may change. If we always
        // reference the latest version of this sequence, items may “disappear” unexpectedly as the
        // user accepts and dismisses recommendations. Preserving the initial sequence as state will
        // keep the item list stable until the user does a reload, minimizing user confusion.
        //
        // Doing this does result in an extra call to `getWizardSequence` at load time, so we only do
        // it if the filter is requested.
        if (withRecommendationsOnly && withRecommendationsSequenceKey) {
          setInitialWithRecommendationsSequence(getWizardSequence(plan)[withRecommendationsSequenceKey])
        }
      } catch (error) {
        if (onError) {
          onError(error)
        }
      }
    }

    retrievePlan()
    return () => {
      active = false
    }
  }, [planId, planGetter, planModeler, withRecommendationsOnly, withRecommendationsSequenceKey, onError])

  // The sequence setter rides along so that flows which add or delete items mid-walkthrough (e.g.,
  // extraction editing) can splice the frozen with-recommendations sequence accordingly.
  return { plan, setPlan, planLoading, initialWithRecommendationsSequence, setInitialWithRecommendationsSequence }
}

const useIepPrint = () => {
  // Print and Save-to-Binder get their own loading flags—rather than one shared `printing`
  // flag—so the dialog can show each action's own "Generating…"/"Saving…" state instead of both
  // buttons reacting (and disabling) whenever either action is in flight.
  const [printing, setPrinting] = useState(false)
  const [savingToBinder, setSavingToBinder] = useState(false)
  const [error, setError] = useState(null)
  const handleApiError = useMemo(() => getHookPromiseErrorHandler(setError), [])

  // Shared by executePrint/executeSaveToBinder below: both look up the plan type's report action
  // and call it with the same payload shape—they differ only in which action they invoke, which
  // loading flag they toggle, and what they do with a successful response.
  const runReportAction = async (actionKey, setLoading, { iepId, planType, options }) => {
    const printFunctions = PLAN_PRINT_FUNCTIONS[planType]
    const action = printFunctions?.[actionKey]

    if (!action) {
      return null
    }

    setLoading(true)
    try {
      return await action(iepId, options)
    } catch (err) {
      handleApiError(err)
      return null
    } finally {
      setLoading(false)
    }
  }

  const executePrint = async ({ iepId, planType, source, options }) => {
    if (!PLAN_PRINT_FUNCTIONS[planType]) {
      return null
    }

    const { notesOnly } = options ?? {}
    trackIepNotesPrinted(planType, iepId, notesOnly ? 'notesOnly' : 'inline', source)
    const response = await runReportAction('reportGenerator', setPrinting, {
      iepId,
      planType,
      options
    })
    return response ? response.url : null
  }

  const executeSaveToBinder = ({ iepId, planType, options }) =>
    runReportAction('reportSaveToBinder', setSavingToBinder, {
      iepId,
      planType,
      options
    })

  return { printing, savingToBinder, error, setError, executePrint, executeSaveToBinder }
}

const useIepProgressPercent = (phase, extractionCompleted, resetKey) => {
  // We model percent-analyzed as state because not every pipeline status maps to a percent value.
  const [percentAnalyzed, setPercentAnalyzed] = useState(0)

  // This is keyed on the reset key alone because a new document starts the percent over but a new pipeline
  // status must not—otherwise, an unrecognized status would wipe out the progress so far.
  useEffect(() => {
    setPercentAnalyzed(0)
  }, [resetKey])

  useEffect(() => {
    // Returning an unchanged value makes React bail out of the re-render, so updating on every status
    // costs nothing when the percent stays the same.
    setPercentAnalyzed(percentAnalyzed => pipelineStatusToPercent(phase, percentAnalyzed, extractionCompleted))
  }, [extractionCompleted, phase])

  return pipelineStatusToPercent(phase, percentAnalyzed, extractionCompleted)
}

// We use this hook to grab the post metadata, IEP data, and background processing status for any given IEP.
// Since different places need to show tracker UI (like the standard IepStatusTracker in goals, or the
// IepWizardStatusTracker when setting up All About Me), this centralizes all the fetching, polling, and
// removal logic in one place so we don't have to duplicate it.
//
// The plan is fetched through injected `planGetter`/`planModeler` functions so that the same
// machinery serves IPPs, whose endpoint and model differ from the IEP defaults but whose extraction
// pipeline, progress stream, and polling contract are the same. Supply both or neither: with only one,
// the other is still chosen from the fetched post, and its change when the post loads refetches the plan.
//
// `onRemove` must reject when the removal fails. On success the caller is expected to unmount the
// tracker or change `iepId`; the “Removing…” scrim stays up until one of those happens.
const useIepStatus = ({
  iepId,
  knownError,
  onReady,
  onRemove,
  onReviewError,
  tags = IEP_DOCUMENT_TAG_IDS,
  planGetter,
  planModeler,
  includeNonSelectable,
  options
}) => {
  const { suppressCompletionEmail, profileTemplateId } = options ?? {}
  const { goalPath, iepPath } = useContext(PlanNavigationContext)
  const { progressUpdates } = useContext(NotificationsContext)

  const location = useLocation()
  const { pathname } = location

  const appUser = useAppUser()
  const canBeNavigator = appUser.role(CAN_BE_NAVIGATOR)
  const { inParentView } = useCurrentSite() ?? {}

  const [pollStatus, setPollStatus] = useState()
  const [post, setPost] = useState()
  const [loadedPostId, setLoadedPostId] = useState()
  const [pendingPosts, setPendingPosts] = useState([])
  const [iep, setIep] = useState()
  const [iepLocation, setIepLocation] = useState()
  const [confirmRemove, setConfirmRemove] = useState(false)
  const [removed, setRemoved] = useState(false)

  const { client, createdAt, description, lastUpdatedAt, referenceTimestamp } = post ?? {}
  const filename = postFilename(post)
  const issueDate = referenceTimestamp ?? lastUpdatedAt ?? createdAt

  const { planType, concerns = [], concernsMap = {}, recommendations = [], recommendationsMap = {} } = iep ?? {}

  const iepRoot = rootedPlanPath(iepPath(iepId, client), planType)
  const parentIepRoot = useHref(rootedPlanPath(`/${client}/edu/${iepId}`, planType))
  const parentOverviewLink = `${window.location.origin}${parentIepRoot}/summary`

  // GET /ieps/<id> launches an IEP extraction if it doesn't already exist, so we can't default to IEP
  // before checking the post's type. If the caller already knows the plan type (planGetter/planModeler
  // supplied), use that immediately. Otherwise wait for the post fetch (which runs anyway, for display
  // metadata) to confirm the document's real type before choosing.
  const isIppDocument = equalArrayWithOrder(post?.documentType, IPP_DOCUMENT_TAG_IDS[1])
  const effectivePlanGetter = planGetter ?? (isIppDocument ? getIpp : getIep)
  const effectivePlanModeler = planModeler ?? (isIppDocument ? model.ipp : model.iep)

  const progressStatus = progressUpdates.find(({ documentId }) => documentId === iepId)
  const {
    extraction_fully_complete: extractionCompleted,
    pipeline_status: status,
    text: statusText
  } = progressStatus ?? {}

  const currentPercent = useIepProgressPercent(status, extractionCompleted, iepId)

  const percentReviewed = getPercentReviewed(iep)
  const inProgress = percentReviewed > 0.0
  const completed = percentReviewed === 100.0

  const { recommendations: visibleRecommendations, concerns: visibleConcerns } = useMemo(
    () =>
      getVisibleRecommendationsAndConcerns({
        recommendationIds: recommendations.map(idMap),
        concernIds: concerns.map(idMap),
        recommendationsMap,
        concernsMap
      }),
    [recommendations, concerns, recommendationsMap, concernsMap]
  )
  const recommendationCount = visibleRecommendations.length
  const visibleConcernCount = visibleConcerns.length

  const showTally = Boolean(iep) && inProgress

  const planVariant = PLAN_VARIANTS[planType] ?? PLAN_DEFAULT
  const { planLabel } = planVariant

  const [error, setError] = useState(knownError)
  const handleApiError = useMemo(() => getHookPromiseErrorHandler(setError), [])

  const { documentTypeTags, tagsLoading } = useDocumentTags({ includeNonSelectable, setError: handleApiError })

  // The metadata tags are determined by pulling out just the non-category tags: i.e., the ones
  // that have parents.
  const metadataTags = tagsLoading
    ? EMPTY_TAGS
    : tags.map(tag => tagFor(tag, documentTypeTags)).filter(typeTag => Boolean(typeTag?.parentId))

  const metadataTagText = metadataTags.map(({ displayName }) => displayName).join(', ')

  const displayPlanLabel = useMemo(() => {
    if (planType) {
      return planType === PLAN_TYPE_504 ? PLAN_LABEL_504 : PLAN_LABEL_IEP
    }
    if (metadataTagText) {
      return metadataTagText
    }
    return PLAN_LABEL_IEP
  }, [planType, metadataTagText])

  const handleCancelRemove = event => setConfirmRemove(false)
  const handleRequestRemove = event => setConfirmRemove(true)

  const handleExecuteRemove = useCallback(
    async event => {
      try {
        setRemoved(true)
        handleCancelRemove(event)
        await onRemove(post)
        // On success, `removed` is deliberately left set: `onRemove` resolves before the parent has
        // dropped this tracker (feed refetch, goal update), so clearing it here would flash the
        // document back in. The tracker unmounts once the parent catches up.
      } catch (error) {
        // The caller reports the failure: it knows what it tried, and it already has a snackbar or
        // dialog for it. This only brings the tracker back so the user can retry. Taking over `error`
        // here would swap the whole tracker into its “We found an issue” state, with no way back.
        setRemoved(false)
      }
    },
    [onRemove, post]
  )

  const handleRemove = useMemo(() => (onRemove ? handleExecuteRemove : undefined), [onRemove, handleExecuteRemove])

  const handleReviewError = useMemo(() => {
    if (!onReviewError) {
      return undefined
    }

    return event => onReviewError(error)
  }, [onReviewError, error])

  const handleTrackReviewStarted = event => trackPlanReviewStarted(planType, iepId, pathToTrackLocation(pathname))

  useEffect(() => {
    setPost(null)
    setLoadedPostId(undefined)

    // Callers that own the hook (rather than the tracker) swap documents through `iepId`, so the
    // flag from the previous document’s removal must not greet the new one.
    setRemoved(false)

    if (!iepId) {
      return
    }

    let active = true

    const retrieveIepPost = async () => {
      try {
        const postResponse = await getPostById(iepId)
        if (active) {
          setPost(model.post(postResponse))
          setLoadedPostId(iepId)
        }
      } catch (errorResponse) {
        handleApiError(errorResponse)
        if (active) {
          setLoadedPostId(iepId)
        }
      }
    }

    retrieveIepPost()
    return () => {
      active = false
    }
  }, [iepId, handleApiError])

  // Guards every in-flight fetch, including ones already waiting out a retry. A ref rather than an
  // effect-local flag because the retrieval below outlives the effect that started it: the
  // completion effect calls the same function, and both need the same generation to be cancelled.
  // A counter rather than a flag because the fetch effect re-arms as soon as it is cleaned up, and a
  // re-armed flag can’t tell the new generation’s responses from those still in flight for the old one.
  const fetchGenerationRef = useRef(0)

  // Collapsed to a boolean so that a post load which doesn’t change the getter doesn’t re-create
  // retrievePlan. That would re-run the fetch effect below and issue a second, identical request.
  //
  // The post must be this plan’s: for one render after iepId changes, the previous plan’s post is
  // still in state, and choosing the getter from it would fetch the new plan as the wrong type.
  const planTypeKnown = Boolean(planGetter) || loadedPostId === iepId

  // Deliberately does not depend on the pipeline status. The retrieval drives itself—a plan that is
  // still extracting answers with HTTP 202 and schedules its own retry—so tying it to the status
  // stream would cancel that chain on every progress message and, for a status the milestones map
  // recognizes, never restart it. The plan would then never load and the tracker would never reach
  // its “ready” state.
  const retrievePlan = useCallback(async () => {
    if (!iepId || knownError || !planTypeKnown) {
      return
    }

    const generation = fetchGenerationRef.current
    const isCurrent = () => fetchGenerationRef.current === generation

    setPollStatus({ status: IEP_STATUS_LOADING })

    try {
      const planResponse = await effectivePlanGetter(iepId, { suppressCompletionEmail, profileTemplateId })
      if (isCurrent()) {
        setIep(effectivePlanModeler(planResponse))
        setPollStatus(null)
      }
    } catch (errorResponse) {
      const { response } = errorResponse
      const { status: responseStatus } = response ?? {}
      if (responseStatus === HTTP_ACCEPTED) {
        const acceptedStatus = await response.json()
        if (isCurrent()) {
          setPollStatus(acceptedStatus)
          setTimeout(() => {
            if (isCurrent()) {
              retrievePlan()
            }
          }, RETRY_DELAY)
        }
      } else if (isCurrent()) {
        handleApiError(errorResponse)
      }
    }
  }, [
    iepId,
    knownError,
    planTypeKnown,
    effectivePlanGetter,
    effectivePlanModeler,
    handleApiError,
    suppressCompletionEmail,
    profileTemplateId
  ])

  useEffect(() => {
    setIep(null)
    setPollStatus(null)

    retrievePlan()

    return () => {
      fetchGenerationRef.current += 1
    }
  }, [retrievePlan])

  // The retry delay is a floor, not a schedule: once the pipeline reports extraction complete the
  // plan is fetchable now, and waiting out the remainder of a retry interval to say so reads as a
  // stall on the one transition the user is watching for.
  //
  // Only the transition into completion refetches, not the completed state itself. A plan that was
  // already extracted before this mounted is fetched by the effect above, and firing on the state
  // would have every tracker of a finished plan—the common case—issue a second identical request.
  const extractionCompletedRef = useRef(extractionCompleted)

  useEffect(() => {
    const justCompleted = extractionCompleted && !extractionCompletedRef.current
    extractionCompletedRef.current = extractionCompleted

    if (justCompleted) {
      retrievePlan()
    }
  }, [extractionCompleted, retrievePlan])

  useEffect(() => {
    const { documentsToExtract } = pollStatus ?? {}
    if (emptyArray(documentsToExtract)) {
      setPendingPosts([])
      return
    }

    let active = true
    const fetchPosts = async () => {
      try {
        const posts = (await Promise.all(documentsToExtract.map(getPostById))).map(model.post)

        if (active) {
          setPendingPosts(posts)
        }
      } catch (error) {
        handleApiError(error)
      }
    }

    fetchPosts()
    return () => {
      active = false
    }
  }, [handleApiError, pollStatus])

  // We run onReady in a separate effect so that changes to the callback (which often changes
  // on every page view/navigation due to pathname/router state) don't trigger retrieveIep again,
  // which would wipe our state and force a new API call every time the user clicks around.
  useEffect(() => {
    if (iep && onReady) {
      setIepLocation(onReady(iep))
    } else {
      setIepLocation(null)
    }
  }, [iep, onReady])

  useEffect(() => {
    setError(knownError)
  }, [knownError])

  return {
    post,
    iep,
    error,
    setError,
    pollStatus,
    pendingPosts,
    progressStatus,
    currentPercent,
    statusText,
    removed,
    confirmRemove,
    handleRequestRemove,
    handleCancelRemove,
    handleRemove,
    handleReviewError,
    handleTrackReviewStarted,
    filename,
    displayPlanLabel,
    planLabel,
    planType,
    iepLocation,
    showTally,
    issueDate,
    iepRoot,
    parentOverviewLink,
    inParentView,
    canBeNavigator,
    completed,
    inProgress,
    percentReviewed,
    recommendationCount,
    visibleConcernCount,
    client,
    createdAt,
    description,
    iepId,
    goalPath,
    IEP_STATUS_MESSAGES,
    metadataTagText
  }
}

export {
  pathToTrackLocation,
  substitutePlanTypeRoot,
  rootedPlanPath,
  planContinuationLocation,
  usePlanTypeNavigationContextProvider,
  useIepAccess,
  useIepPrint,
  useIepProgressPercent,
  useIepSectionEditDrawer,
  useIepStatus,
  useIepUpgrade,
  usePlanItemCoordination,
  usePlanItemScrollPadding,
  usePlanLoad,
  useWizardSetup
}
